import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.js';

// Async thunk that calls real Django API
export const incrementAsync = createAsyncThunk(
  'counter/incrementAsync',
  async (amount, { getState, rejectWithValue }) => {
    try {
      const currentValue = getState().counter.value;
      const newValue = currentValue + amount;
      
      // Send update to Django backend
      const response = await apiService.post('/test/', { 
        action: 'increment',
        amount: amount,
        current_value: currentValue,
        new_value: newValue
      });
      
      return amount; // Return the increment amount
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch counter value from Django API
export const fetchCounterValue = createAsyncThunk(
  'counter/fetchCounterValue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.testConnection({ action: 'get_random_value' });
      
      // If the response contains a value, use it; otherwise generate random
      if (response.random_value !== undefined) {
        return response.random_value;
      }
      return Math.floor(Math.random() * 100);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// New thunk to test Django connection
export const testConnection = createAsyncThunk(
  'counter/testConnection',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.healthCheck();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const counterSlice = createSlice({
  name: 'counter',
  initialState: {
    value: 0,
    loading: false,
    error: null,
    connectionStatus: null,
    lastWebSocketUpdate: null,
  },
  reducers: {
    increment: (state) => {
      state.value += 1;
    },
    decrement: (state) => {
      state.value -= 1;
    },
    incrementByAmount: (state, action) => {
      state.value += action.payload;
    },
    reset: (state) => {
      state.value = 0;
      state.error = null;
    },
    clearConnectionStatus: (state) => {
      state.connectionStatus = null;
    },
    updateFromWebSocket: (state, action) => {
      state.value = action.payload.counter_value;
      state.lastWebSocketUpdate = {
        timestamp: action.payload.timestamp,
        message: action.payload.message,
        receivedAt: new Date().toISOString()
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // incrementAsync cases
      .addCase(incrementAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(incrementAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.value += action.payload;
      })
      .addCase(incrementAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchCounterValue cases
      .addCase(fetchCounterValue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCounterValue.fulfilled, (state, action) => {
        state.loading = false;
        state.value = action.payload;
      })
      .addCase(fetchCounterValue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // testConnection cases
      .addCase(testConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testConnection.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionStatus = action.payload;
      })
      .addCase(testConnection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.connectionStatus = { error: action.payload };
      })
      // Handle WebSocket counter updates from websocketSlice
      .addCase('websocket/receiveCounterUpdate', (state, action) => {
        state.value = action.payload.counter_value;
        state.lastWebSocketUpdate = {
          timestamp: action.payload.timestamp,
          message: action.payload.message,
          receivedAt: new Date().toISOString()
        };
      });
  },
});

export const { increment, decrement, incrementByAmount, reset, clearConnectionStatus, updateFromWebSocket } = counterSlice.actions;

export default counterSlice.reducer;